import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ScrollView, View } from 'react-native';
import { useRouter } from 'expo-router';
import { ActivityIndicator, Button, Card, SegmentedButtons, Text, TextInput, useTheme } from 'react-native-paper';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/src/context/AuthContext';
import { apiFetch } from '@/src/api/apiService';
import { API_BASE } from '@/src/api/config';
import { Logo } from '@/src/components/Logo';

type Outcome = 'idle' | 'pending' | 'active' | 'suspended' | 'rejected' | 'unknown';

/** The two D-057 standardized partner VAT regimes. */
const VAT_NORMAL = 'normal';
const VAT_EXEMPT = 'exempt';

/** Signed-out journeys: register a new account, or sign in to an existing one. */
type Mode = 'new' | 'existing';

/** Mirrors Django's default MinimumLengthValidator; the backend stays the authority. */
const PASSWORD_MIN_LENGTH = 8;

interface ApplyState {
  applied?: boolean | null;
  status?: 'pending' | 'approved' | 'rejected' | null;
  partner_status?: 'active' | 'suspended' | 'pending' | null;
  /** The account's own NIF, masked; present when the account already has one. */
  account_nif_masked?: string | null;
}

/**
 * Pure truth map from the authoritative GET /api/referrals/apply/ response to
 * the screen outcome. Only the EXACT trusted "no application" shape
 * ({applied:false, status:null, partner_status:null}) is IDLE; an empty body,
 * an absent/null marker, a missing live partner or any unrecognised value is
 * UNKNOWN — never silently "not applied" or "pending" (D-054).
 */
export function mapApplyState(response: { ok: boolean; data?: ApplyState | null }): { outcome: Outcome; known: boolean } {
  if (!response.ok || !response.data) return { outcome: 'unknown', known: false };
  const { applied, status, partner_status: partnerStatus } = response.data;
  // Exact trusted no-application shape only.
  if (applied === false && status == null && partnerStatus == null) return { outcome: 'idle', known: true };
  // Any other body must carry the explicit "applied" marker; a missing or
  // inconsistent marker is not something we can render truthfully.
  if (applied !== true) return { outcome: 'unknown', known: false };
  if (partnerStatus === 'active') return { outcome: 'active', known: true };
  if (partnerStatus === 'suspended') return { outcome: 'suspended', known: true };
  if (status === 'rejected') return { outcome: 'rejected', known: true };
  if (status === 'pending' || partnerStatus === 'pending') return { outcome: 'pending', known: true };
  // e.g. status 'approved' with no live active/suspended partner is an
  // inconsistent/transitional state — never claim Admin is still reviewing.
  return { outcome: 'unknown', known: false };
}

// Client mirror of the backend NIF/NIPC check (backend remains the authority,
// D-057 2026-10-24): nine digits, a prefix the Portuguese fiscal-number rules
// accept (1, 2, 3 natural persons; 5, 6, 8, 9 legal entities) and a valid
// check digit. Never proof of identity or company representation.
const FISCAL_ID_FIRST_DIGITS = '1235689';

function nifLooksValid(raw: string): boolean {
  const value = (raw || '').trim();
  if (!/^\d{9}$/.test(value)) return false;
  if (!FISCAL_ID_FIRST_DIGITS.includes(value[0])) return false;
  let sum = 0;
  for (let i = 0; i < 8; i += 1) sum += Number(value[i]) * (9 - i);
  let check = 11 - (sum % 11);
  if (check >= 10) check = 0;
  return check === Number(value[8]);
}

function emailLooksValid(raw: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((raw || '').trim());
}

/**
 * Partner application (D-057 dedicated partner registration).
 *
 * Signed out, the visitor picks one of two journeys:
 * - NEW account: email, password (+ confirmation, checked by the backend's
 *   password validators), NIF/NIPC and the explicit VAT regime. Possession of
 *   the email is proven through a single-use link (see /partner-verify); only
 *   then is the account created with a PENDING application.
 * - EXISTING account: sign in with the password or an email access link; the
 *   application then reuses that SAME account.
 * This journey never creates a driver/owner account, a company, or a
 * subscription, and confirming the email never approves the partner.
 *
 * Signed in, the user declares the VAT regime and a NIF/NIPC only when the
 * account has none yet; approval is a separate internal Admin action. The shown
 * state always comes from the authoritative GET /api/referrals/apply/.
 */
export default function PartnerApply() {
  const { t } = useTranslation('partner');
  const theme = useTheme();
  const router = useRouter();
  const { userToken, isLoading, login } = useAuth();
  const [outcome, setOutcome] = useState<Outcome>('idle');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [known, setKnown] = useState(true);
  const [resolved, setResolved] = useState(false);
  // Which email went out: a registration confirmation or an access link.
  const [sent, setSent] = useState<'registration' | 'access' | null>(null);
  const [mode, setMode] = useState<Mode>('new');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [accountNifMasked, setAccountNifMasked] = useState<string | null>(null);
  const [nif, setNif] = useState('');
  const [vatRegime, setVatRegime] = useState('');
  const [existingNif, setExistingNif] = useState('');
  const [existingVatRegime, setExistingVatRegime] = useState('');
  // Generation fence: every status load captures a generation and drops its
  // response if the account changed, the screen unmounted, or a newer load began.
  const genRef = useRef(0);
  // Synchronous duplicate-submit guard (the `busy` state only drives the spinner).
  const busyRef = useRef(false);
  // Owner of the current in-flight guard: a stale operation's cleanup must not
  // release a guard that a newer account/operation now holds.
  const opRef = useRef(0);

  const loadStatus = useCallback(async () => {
    const gen = ++genRef.current;
    setResolved(false);
    let next: { outcome: Outcome; known: boolean };
    let nifMasked: string | null = null;
    try {
      const response = await apiFetch<ApplyState>(`${API_BASE}/api/referrals/apply/`, { includeCompanyContext: false }, true, false);
      next = mapApplyState(response);
      const masked = response.data?.account_nif_masked;
      if (next.known && typeof masked === 'string' && masked) nifMasked = masked;
    } catch {
      next = { outcome: 'unknown', known: false };
    }
    if (gen !== genRef.current) return;
    setAccountNifMasked(nifMasked);
    setOutcome(next.outcome);
    setKnown(next.known);
    setResolved(true);
  }, []);

  // Any account change (login, logout, switch) drops in-flight responses and
  // clears every piece of the previous account's state before loading anew.
  useEffect(() => {
    genRef.current += 1;
    opRef.current += 1;
    busyRef.current = false;
    setBusy(false);
    setEmail(''); setNif(''); setVatRegime(''); setExistingNif(''); setExistingVatRegime('');
    setPassword(''); setPasswordConfirm(''); setAccountNifMasked(null); setMode('new');
    setOutcome('idle'); setKnown(true); setResolved(false);
    setError(''); setSent(null);
    if (userToken) void loadStatus();
    return () => { genRef.current += 1; };
  }, [userToken, loadStatus]);

  // Returns the operation id that owns the guard, or 0 when already busy.
  const begin = (): number => {
    if (busyRef.current) return 0;
    busyRef.current = true;
    opRef.current += 1;
    setBusy(true);
    return opRef.current;
  };
  const end = (op: number) => {
    if (op !== opRef.current) return;
    busyRef.current = false;
    setBusy(false);
  };

  // Paper draws each segment label on a single line, so the labels stay short
  // and the VAT rate lives in the help line under the selector.
  const regimeButtons = [
    { value: VAT_NORMAL, label: t('applyVatNormal'), testID: 'apply-vat-normal' },
    { value: VAT_EXEMPT, label: t('applyVatExempt'), testID: 'apply-vat-exempt' },
  ];
  const modeButtons = [
    { value: 'new', label: t('applyModeNew'), testID: 'apply-mode-new' },
    { value: 'existing', label: t('applyModeExisting'), testID: 'apply-mode-existing' },
  ];

  const switchMode = (next: string) => {
    if (busyRef.current) return;
    setMode(next === 'existing' ? 'existing' : 'new');
    setPassword(''); setPasswordConfirm(''); setError('');
  };

  // Public email+password+NIF+regime request. The backend response is deliberately
  // generic, so the UI must never imply whether an account already exists for the email.
  // Only a real success claims "email sent" (D-054); 401/403/404 (including a
  // default-disabled endpoint) surface a truthful, recoverable error.
  const request = async () => {
    if (busyRef.current) return;
    setError('');
    if (!emailLooksValid(email)) { setError(t('applyEmailInvalid')); return; }
    if (!password) { setError(t('applyPasswordRequired')); return; }
    // Sign-in trims surrounding spaces, so they are refused here (as the backend does).
    if (password !== password.trim()) { setError(t('applyPasswordWhitespace')); return; }
    if (password.length < PASSWORD_MIN_LENGTH) { setError(t('applyPasswordWeak')); return; }
    if (password !== passwordConfirm) { setError(t('applyPasswordMismatch')); return; }
    if (!nifLooksValid(nif)) { setError(t('applyNifInvalid')); return; }
    // The VAT regime is an explicit declaration, never inferred from the NIF.
    if (vatRegime !== VAT_NORMAL && vatRegime !== VAT_EXEMPT) { setError(t('applyVatRequired')); return; }
    const op = begin();
    if (!op) return;
    const gen = genRef.current;
    try {
      const response = await apiFetch<Record<string, unknown>>(`${API_BASE}/api/referrals/partner-registration/request/`, {
        method: 'POST',
        includeCompanyContext: false,
        body: JSON.stringify({
          email: email.trim(), nif: nif.trim(), vat_regime: vatRegime,
          password, password_confirm: passwordConfirm,
        }),
      }, false, false);
      if (gen !== genRef.current) return;
      if (response.status === 400) {
        const fields = response.data || {};
        if (fields.password) setError(t(password !== password.trim() ? 'applyPasswordWhitespace' : 'applyPasswordWeak'));
        else if (fields.password_confirm) setError(t('applyPasswordMismatch'));
        else if (fields.email) setError(t('applyEmailInvalid'));
        else setError(t('applyNifInvalid'));
        return;
      }
      if (!response.ok) { setError(t('applyError')); return; }
      setPassword(''); setPasswordConfirm('');
      setSent('registration');
    } catch { if (gen === genRef.current) setError(t('applyError')); }
    finally { end(op); }
  };

  // Existing account, password sign-in. A successful login changes the auth
  // scope, which reloads the authoritative application state for that account.
  const signIn = async () => {
    if (busyRef.current) return;
    setError('');
    if (!emailLooksValid(email)) { setError(t('applyEmailInvalid')); return; }
    if (!password) { setError(t('applyPasswordRequired')); return; }
    const op = begin();
    if (!op) return;
    const gen = genRef.current;
    try {
      const result = await login(email.trim(), password);
      if (gen !== genRef.current) return;
      setPassword('');
      if (!result?.success) setError(t('applyLoginFailed'));
    } catch { if (gen === genRef.current) setError(t('applyLoginFailed')); }
    finally { end(op); }
  };

  // Authenticated existing user: reuse the same account, declare the NIF and the
  // explicit VAT regime, then re-read the authoritative state instead of
  // assuming "pending".
  const applyAuthenticated = async () => {
    if (busyRef.current) return;
    setError('');
    // D-057 (2026-10-16): the new-account UI REQUIRES an explicit VAT-regime
    // declaration — the backend keeps the field optional only for legacy
    // clients, not for this screen. The regime is never defaulted and never
    // inferred from the identifier; without an explicit choice nothing is submitted.
    if (existingVatRegime !== VAT_NORMAL && existingVatRegime !== VAT_EXEMPT) { setError(t('applyVatRequired')); return; }
    // A NIF is asked for only when the account has none yet; an account's
    // existing NIF is reused by the backend and never overwritten (D-050).
    if (!accountNifMasked && !nifLooksValid(existingNif)) { setError(t('applyNifInvalid')); return; }
    if (!userToken) return;
    const op = begin();
    if (!op) return;
    const gen = genRef.current;
    // Bind the submission to the account that pressed apply: storage may hold a
    // different account's token by the time the request is built.
    const authorization = `Token ${userToken}`;
    try {
      // Explicit, minimal payload: the NIF is sent only when the account has
      // none (an existing fiscal identity is never overwritten, D-050); the
      // regime is always the explicitly selected declaration — never inferred.
      const payload: { nif?: string; vat_regime: string } = { vat_regime: existingVatRegime };
      if (!accountNifMasked && existingNif) payload.nif = existingNif.trim();
      const response = await apiFetch(`${API_BASE}/api/referrals/apply/`, {
        method: 'POST',
        includeCompanyContext: false,
        headers: { Authorization: authorization },
        body: JSON.stringify(payload),
      }, true, false);
      if (gen !== genRef.current) return;
      if (response.status === 400) {
        // The server keeps the account's (or the application's) stored NIF
        // authoritative (nif_conflict); the stored value is never changed.
        const data = response.data as { code?: string; nif_source?: string } | null;
        setError(t(data?.code !== 'nif_conflict' ? 'applyNifInvalid'
          : data.nif_source === 'application' ? 'applyNifConflictApplication' : 'applyNifConflict'));
        return;
      }
      if (!response.ok) { setError(t('applyError')); return; }
      await loadStatus();
    } catch { if (gen === genRef.current) setError(t('applyError')); }
    finally { end(op); }
  };

  // Existing users may also sign in by email (they may have no usable password,
  // e.g. a bare partner account). Uses the same generic response contract and
  // returns to this screen.
  const requestAccess = async () => {
    if (busyRef.current) return;
    setError('');
    if (!emailLooksValid(email)) { setError(t('applyEmailInvalid')); return; }
    const op = begin();
    if (!op) return;
    const gen = genRef.current;
    try {
      const response = await apiFetch(`${API_BASE}/api/referrals/partner-access/request/`,
        { method: 'POST', includeCompanyContext: false, body: JSON.stringify({ email: email.trim(), return_to: 'partner-apply' }) }, false, false);
      if (gen !== genRef.current) return;
      if (response.status === 400) { setError(t('applyEmailInvalid')); return; }
      if (!response.ok) { setError(t('applyError')); return; }
      setPassword('');
      setSent('access');
    } catch { if (gen === genRef.current) setError(t('applyError')); }
    finally { end(op); }
  };

  return <ScrollView style={{ backgroundColor: theme.colors.background }} contentContainerStyle={{ padding: 16, gap: 16, maxWidth: 640, width: '100%', alignSelf: 'center' }}>
    <View testID="partner-brand" accessible accessibilityLabel="AllDrivers"><Logo size="medium" /></View>
    <Text variant="headlineMedium">{t('applyTitle')}</Text>
    {isLoading || (!!userToken && !resolved) ? <ActivityIndicator /> : !userToken ? (sent ? <Card style={{ borderRadius: 16 }}><Card.Content style={{ gap: 12 }}>
      <Text accessibilityRole="alert" testID="apply-sent">{t(sent === 'access' ? 'applyAccessSentTitle' : 'applySentTitle')}</Text>
      <Text variant="bodyMedium">{t(sent === 'access' ? 'applyAccessSentHelp' : 'applySentHelp')}</Text>
      <Button mode="text" contentStyle={{ minHeight: 44 }} testID="apply-sent-back"
        onPress={() => setSent(null)}>{t('applySentBack')}</Button>
    </Card.Content></Card> : <Card style={{ borderRadius: 16 }}><Card.Content style={{ gap: 12 }}>
      <Text>{t('applyIntro')}</Text>
      <Text variant="bodySmall" style={{ color: theme.colors.onSurfaceVariant }}>{t('applyDistinct')}</Text>
      <SegmentedButtons value={mode} onValueChange={switchMode} buttons={modeButtons} />
      <TextInput label={t('applyEmail')} accessibilityLabel={t('applyEmail')} value={email} onChangeText={setEmail}
        autoCapitalize="none" keyboardType="email-address" autoComplete="email" testID="apply-email" disabled={busy} />
      <TextInput label={t('password')} accessibilityLabel={t('password')} value={password} onChangeText={setPassword}
        secureTextEntry autoCapitalize="none" autoComplete={mode === 'new' ? 'new-password' : 'current-password'}
        testID="apply-password" disabled={busy} />
      {mode === 'new' ? <>
        <TextInput label={t('applyPasswordConfirm')} accessibilityLabel={t('applyPasswordConfirm')} value={passwordConfirm}
          onChangeText={setPasswordConfirm} secureTextEntry autoCapitalize="none" autoComplete="new-password"
          testID="apply-password-confirm" disabled={busy} />
        <Text variant="bodySmall" style={{ color: theme.colors.onSurfaceVariant }}>{t('applyPasswordHelp')}</Text>
        <TextInput label={t('applyNif')} accessibilityLabel={t('applyNif')} value={nif} onChangeText={setNif}
          keyboardType="number-pad" maxLength={9} testID="apply-nif" disabled={busy} />
        <Text variant="bodySmall">{t('applyVatLabel')}</Text>
        <SegmentedButtons value={vatRegime} onValueChange={setVatRegime} buttons={regimeButtons} />
        <Text variant="bodySmall" style={{ color: theme.colors.onSurfaceVariant }}>{t('applyVatHelp')}</Text>
        {!!error && <Text accessibilityRole="alert" style={{ color: theme.colors.error }}>{error}</Text>}
        <Button mode="contained" contentStyle={{ minHeight: 44 }} loading={busy} disabled={busy}
          testID="apply-submit" onPress={() => void request()}>{t('applyRequestSubmit')}</Button>
      </> : <>
        {!!error && <Text accessibilityRole="alert" style={{ color: theme.colors.error }}>{error}</Text>}
        <Button mode="contained" contentStyle={{ minHeight: 44 }} loading={busy} disabled={busy}
          testID="apply-login-submit" onPress={() => void signIn()}>{t('applySignIn')}</Button>
        <Text variant="bodySmall" style={{ color: theme.colors.onSurfaceVariant }}>{t('emailLinkHelp')}</Text>
        <Button mode="outlined" icon="email-outline" contentStyle={{ minHeight: 44 }} disabled={busy}
          testID="apply-access" onPress={() => void requestAccess()}>{t('emailLinkSignIn')}</Button>
      </>}
    </Card.Content></Card>) : !known ? <Card style={{ borderRadius: 16 }}><Card.Content style={{ gap: 12 }}>
      <Text accessibilityRole="alert" testID="apply-unknown" style={{ color: theme.colors.error }}>{t('error')}</Text>
      <Button contentStyle={{ minHeight: 44 }} testID="apply-retry" onPress={() => void loadStatus()}>{t('retry')}</Button>
    </Card.Content></Card> : <Card style={{ borderRadius: 16 }}><Card.Content style={{ gap: 12 }}>
      {outcome === 'idle' && <>
        <Text>{t('applyIntro')}</Text>
        <Text variant="bodySmall" style={{ color: theme.colors.onSurfaceVariant }}>{t('applyDistinct')}</Text>
        {accountNifMasked
          ? <Text testID="apply-account-nif">{t('applyAccountNif', { nif: accountNifMasked })}</Text>
          : <TextInput label={t('applyNif')} accessibilityLabel={t('applyNif')} value={existingNif} onChangeText={setExistingNif}
            keyboardType="number-pad" maxLength={9} testID="apply-nif" disabled={busy} />}
        <Text variant="bodySmall">{t('applyVatLabel')}</Text>
        <SegmentedButtons value={existingVatRegime} onValueChange={setExistingVatRegime} buttons={regimeButtons} />
        <Text variant="bodySmall" style={{ color: theme.colors.onSurfaceVariant }}>{t('applyVatHelp')}</Text>
        {!!error && <Text accessibilityRole="alert" style={{ color: theme.colors.error }}>{error}</Text>}
        <Button mode="contained" contentStyle={{ minHeight: 44 }} loading={busy} disabled={busy}
          testID="apply-submit" onPress={() => void applyAuthenticated()}>{t('applySubmit')}</Button>
      </>}
      {outcome === 'pending' && <>
        <Text accessibilityRole="alert" testID="apply-pending">{t('applyPending')}</Text>
        <Text variant="bodySmall" style={{ color: theme.colors.onSurfaceVariant }}>{t('applyPendingHelp')}</Text>
      </>}
      {outcome === 'active' && <>
        <Text testID="apply-active">{t('applyActive')}</Text>
        <Button mode="contained" contentStyle={{ minHeight: 44 }} onPress={() => router.push('/partner' as never)}>{t('applyOpenPortal')}</Button>
      </>}
      {outcome === 'suspended' && <Text accessibilityRole="alert" testID="apply-suspended">{t('applySuspended')}</Text>}
      {outcome === 'rejected' && <>
        <Text accessibilityRole="alert" testID="apply-rejected">{t('applyRejected')}</Text>
        <Text variant="bodySmall" style={{ color: theme.colors.onSurfaceVariant }}>{t('applyRejectedHelp')}</Text>
      </>}
    </Card.Content></Card>}
  </ScrollView>;
}
